import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Package, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { AttentionStrip, EmptyState } from '../../components/ui';
import { equipmentAPI } from '../../services/api';
import { format } from 'date-fns';
import OperationsTabs from './OperationsTabs';
import AssetTabs from '../assets/AssetTabs';
import { getEffectiveNextServiceDue, daysUntil } from './serviceDue';

const STATUS_COLORS = {
  ACTIVE: 'bg-ga-success/15 text-ga-success ring-1 ring-ga-success/30',
  UNDER_REPAIR: 'bg-ga-bg-surface-subtle text-ga-text-muted ring-1 ring-ga-border-subtle',
  OUT_OF_SERVICE: 'bg-ga-danger/15 text-ga-danger ring-1 ring-ga-danger/30',
};

const DISPOSAL_COLORS = {
  DISPOSED: 'bg-ga-text-subtle/20 text-ga-text-muted',
  SOLD: 'bg-ga-warning/15 text-ga-warning',
  WRITTEN_OFF: 'bg-ga-danger/15 text-ga-danger',
};

const getServiceDueBadge = (nextDue, status, disposalStatus) => {
  if (disposalStatus) return null;
  if (status !== 'ACTIVE' || !nextDue) return null;
  const diff = daysUntil(nextDue);
  if (diff < 0) return { label: 'Overdue', cls: 'bg-ga-danger/15 text-ga-danger ring-1 ring-ga-danger/30' };
  if (diff <= 7) return { label: 'Due Soon', cls: 'bg-ga-warning/15 text-ga-warning ring-1 ring-ga-warning/30' };
  return { label: 'On Track', cls: 'bg-ga-success/15 text-ga-success ring-1 ring-ga-success/30' };
};

/** When true, renders under Asset Management nav (/dashboard/assets/fixed). */
export default function EquipmentList({ assetMode = false }) {
  const basePath = assetMode ? '/dashboard/assets/fixed' : '/dashboard/operations/equipment';

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [disposalFilter, setDisposalFilter] = useState('active');
  const [page, setPage] = useState(1);
  const limit = 10;

  const fetchList = async () => {
    setLoading(true);
    try {
      const filters = { search, status, page, limit };
      if (disposalFilter === 'active') filters.disposal_status = 'none';
      else if (disposalFilter !== 'all') filters.disposal_status = disposalFilter;

      const res = await equipmentAPI.list(filters);
      setList(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [search, status, disposalFilter, page]);

  const totalPages = Math.ceil(total / limit) || 1;

  const serviceDueCount = list.filter((eq) => {
    if (eq.disposal_status || eq.status !== 'ACTIVE') return false;
    const { date } = getEffectiveNextServiceDue(eq);
    if (!date) return false;
    return daysUntil(date) <= 7;
  }).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Package className="h-7 w-7 text-ga-primary" />
              {assetMode ? 'Fixed Assets' : 'Equipment Management'}
            </h1>
            <p className="text-ga-text-muted mt-1">
              {assetMode ? 'Mowers, rollers, scoreboards, and facility equipment' : 'Manage facility equipment and maintenance'}
            </p>
          </div>
          <Link to={`${basePath}/new`} className="btn-primary-solid inline-flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Asset
          </Link>
        </div>

        {assetMode ? <AssetTabs active="fixed" /> : <OperationsTabs active="equipment" />}

        {serviceDueCount > 0 && (
          <AttentionStrip
            icon={AlertTriangle}
            tone="warning"
            title={`${serviceDueCount} asset${serviceDueCount === 1 ? '' : 's'} due for service`}
            message="Next service is overdue or falls within the next 7 days."
          />
        )}

        <div className="ga-card rounded-xl shadow-sm border border-ga-border-subtle p-4">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ga-text-subtle" />
              <input
                type="text"
                placeholder="Search by name, asset code, brand, location..."
                aria-label="Search equipment"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="ga-input w-full pl-10 pr-4 py-2"
              />
            </div>
            <div className="flex flex-col sm:w-44">
              <label htmlFor="eq-operational-status" className="text-xs font-medium text-ga-text-muted mb-1">
                Operational status
              </label>
              <select
                id="eq-operational-status"
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="ga-input w-full px-4 py-2"
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="UNDER_REPAIR">Under Repair</option>
                <option value="OUT_OF_SERVICE">Out of Service</option>
              </select>
            </div>
            <div className="flex flex-col sm:w-48">
              <label htmlFor="eq-lifecycle-status" className="text-xs font-medium text-ga-text-muted mb-1">
                Lifecycle
              </label>
              <select
                id="eq-lifecycle-status"
                value={disposalFilter}
                onChange={(e) => { setDisposalFilter(e.target.value); setPage(1); }}
                className="ga-input w-full px-4 py-2"
              >
                <option value="active">In service only</option>
                <option value="all">All incl. disposed</option>
                <option value="DISPOSED">Disposed</option>
                <option value="SOLD">Sold</option>
                <option value="WRITTEN_OFF">Written off</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-ga-text-subtle">Loading...</div>
          ) : list.length === 0 ? (
            <EmptyState
              card
              icon={Package}
              title="No assets found"
              description="Add your first fixed asset to start tracking maintenance."
              action={
                <Link to={`${basePath}/new`} className="btn-primary-solid inline-flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add Asset
                </Link>
              }
            />
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {list.map((eq) => {
                  const { date: nextDue } = getEffectiveNextServiceDue(eq);
                  const dueBadge = getServiceDueBadge(nextDue, eq.status, eq.disposal_status);
                  return (
                    <Link
                      key={eq.id}
                      to={`${basePath}/${eq.id}`}
                      className="block ga-card p-4 border border-ga-border-subtle hover:border-ga-primary/40"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-ga-text-primary truncate">{eq.equipment_name}</p>
                          <p className="text-sm text-ga-text-muted">{eq.asset_code}</p>
                        </div>
                        {dueBadge && (
                          <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${dueBadge.cls}`}>
                            {dueBadge.label}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm text-ga-text-muted">
                        {eq.location && <span>{eq.location}</span>}
                        {eq.assigned_staff_name && <span>{eq.assigned_staff_name}</span>}
                        {nextDue && !eq.disposal_status && (
                          <span>Service {format(nextDue, 'dd MMM yyyy')}</span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ga-border-subtle">
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Asset</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Name</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Location</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Assigned</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Status</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Next Service</th>
                      <th className="text-right py-3 px-2 font-semibold text-ga-text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((eq) => {
                      const { date: nextDue, derived } = getEffectiveNextServiceDue(eq);
                      const dueBadge = getServiceDueBadge(nextDue, eq.status, eq.disposal_status);
                      return (
                        <tr key={eq.id} className="border-b border-ga-border-subtle/50 hover:bg-ga-bg-surface-subtle/50">
                          <td className="py-3 px-2 font-mono text-sm text-ga-text-primary">{eq.asset_code}</td>
                          <td className="py-3 px-2">
                            <Link to={`${basePath}/${eq.id}`} className="text-ga-primary hover:underline font-medium">
                              {eq.equipment_name}
                            </Link>
                            <p className="text-xs text-ga-text-subtle">{eq.equipment_type || ''}</p>
                          </td>
                          <td className="py-3 px-2 text-ga-text-muted">{eq.location || '-'}</td>
                          <td className="py-3 px-2 text-ga-text-muted">{eq.assigned_staff_name || '-'}</td>
                          <td className="py-3 px-2">
                            {eq.disposal_status ? (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${DISPOSAL_COLORS[eq.disposal_status] || ''}`}>
                                {eq.disposal_status.replace('_', ' ')}
                              </span>
                            ) : (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[eq.status] || 'bg-ga-bg-surface-subtle text-ga-text-muted'}`}>
                                {eq.status?.replace('_', ' ')}
                              </span>
                            )}
                            {dueBadge && (
                              <span className={`ml-1 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${dueBadge.cls}`}>
                                {dueBadge.label}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-ga-text-muted">
                            {eq.disposal_status ? '-' : (nextDue ? format(nextDue, 'dd MMM yyyy') : '-')}
                            {derived && nextDue && !eq.disposal_status && (
                              <span className="ml-1 text-xs text-ga-text-subtle" title="Estimated from service interval">(est.)</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Link to={`${basePath}/${eq.id}`} className="text-ga-primary hover:underline text-sm font-medium">
                              View
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-ga-border-subtle">
                  <span className="text-sm text-ga-text-muted">Page {page} of {totalPages} ({total} total)</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      aria-label="Previous page"
                      className="p-2 rounded-lg border border-ga-border-subtle disabled:opacity-50 hover:bg-ga-bg-surface-subtle text-ga-text-primary"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      aria-label="Next page"
                      className="p-2 rounded-lg border border-ga-border-subtle disabled:opacity-50 hover:bg-ga-bg-surface-subtle text-ga-text-primary"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
